import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, CheckCircle, Loader2, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { supabase } from '@/lib/supabase'
import { BrandLogo } from '@/components/shared/BrandLogo'
import { useSEO } from '@/hooks/useSEO'

const resetSchema = z
  .object({
    password: z
      .string()
      .min(8, 'A senha deve ter pelo menos 8 caracteres')
      .regex(/[A-Z]/, 'Inclua pelo menos uma letra maiúscula')
      .regex(/[0-9]/, 'Inclua pelo menos um número'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'As senhas não conferem',
    path: ['confirmPassword'],
  })

type ResetFormData = z.infer<typeof resetSchema>

export default function ResetPasswordPage() {
  useSEO({ title: 'Redefinir senha', noIndex: true })
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [done, setDone] = useState(false)
  const [sessionReady, setSessionReady] = useState(false)
  const [sessionError, setSessionError] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetFormData>({ resolver: zodResolver(resetSchema) })

  // Supabase sends a PKCE recovery token in the URL hash.
  // onAuthStateChange fires with event 'PASSWORD_RECOVERY' once it's exchanged.
  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSessionReady(true)
      }
    })

    // If there's already a valid session (user came from the email link),
    // mark as ready immediately
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setSessionReady(true)
      else {
        // Give it 2 seconds for the hash exchange to complete
        setTimeout(() => {
          supabase.auth.getSession().then(({ data: d }) => {
            if (d.session) setSessionReady(true)
            else setSessionError(true)
          })
        }, 2000)
      }
    })

    return () => { listener.subscription.unsubscribe() }
  }, [])

  const onSubmit = async (values: ResetFormData) => {
    const { error } = await supabase.auth.updateUser({ password: values.password })
    if (error) {
      toast.error('Erro ao redefinir senha: ' + error.message)
      return
    }
    setDone(true)
    toast.success('Senha redefinida com sucesso!')
    setTimeout(() => navigate('/login'), 2500)
  }

  const passwordValue = watch('password') ?? ''
  const strength = [
    passwordValue.length >= 8,
    /[A-Z]/.test(passwordValue),
    /[0-9]/.test(passwordValue),
    /[^A-Za-z0-9]/.test(passwordValue),
  ]
  const strengthScore = strength.filter(Boolean).length
  const strengthLabel = ['', 'Fraca', 'Razoável', 'Boa', 'Forte'][strengthScore]
  const strengthColor = ['', 'bg-red-400', 'bg-yellow-400', 'bg-brand-400', 'bg-green-500'][strengthScore]

  // ── Success state ────────────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-950 to-teal-900 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-9 w-9 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-2">Senha redefinida!</h2>
          <p className="text-muted-foreground mb-6">
            Sua senha foi alterada com sucesso. Redirecionando para o login...
          </p>
          <Button asChild className="w-full">
            <Link to="/login">Ir para o login</Link>
          </Button>
        </div>
      </div>
    )
  }

  // ── Invalid/expired token ────────────────────────────────────────────────────
  if (sessionError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-950 to-teal-900 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="h-9 w-9 text-red-500" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-2">Link inválido ou expirado</h2>
          <p className="text-muted-foreground mb-6">
            Este link de redefinição de senha expirou ou já foi usado. Solicite um novo.
          </p>
          <Button asChild className="w-full">
            <Link to="/login">Voltar ao login</Link>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-brand-950 via-brand-900 to-teal-900">
      {/* Left panel — branding */}
      <div className="hidden lg:flex flex-col justify-center items-center flex-1 p-12 text-white">
        <BrandLogo onDark className="h-11 mb-8" />
        <h2 className="text-2xl font-semibold text-center max-w-xs leading-snug">
          Crie uma nova senha segura para sua conta
        </h2>
        <p className="text-brand-200 mt-3 text-center max-w-xs">
          Escolha uma senha forte para manter sua conta protegida.
        </p>
      </div>

      {/* Right panel — form */}
      <div className="flex flex-1 items-center justify-center p-4 lg:p-12 bg-white lg:rounded-l-3xl">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <BrandLogo className="h-9 mb-8 lg:hidden" />

          <h1 className="text-2xl font-bold mb-1">Redefinir senha</h1>
          <p className="text-muted-foreground mb-8 text-sm">
            {sessionReady
              ? 'Digite sua nova senha abaixo.'
              : 'Verificando o link de redefinição...'}
          </p>

          {!sessionReady ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              {/* New password */}
              <div className="space-y-2">
                <Label htmlFor="password">Nova senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Mínimo 8 caracteres"
                    className="pr-10"
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                {/* Strength bar */}
                {passwordValue.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex gap-1">
                      {[0, 1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className={`h-1.5 flex-1 rounded-full transition-all ${
                            i < strengthScore ? strengthColor : 'bg-muted'
                          }`}
                        />
                      ))}
                    </div>
                    <p className={`text-xs font-medium ${
                      strengthScore <= 1 ? 'text-red-500' :
                      strengthScore === 2 ? 'text-yellow-600' :
                      strengthScore === 3 ? 'text-brand-600' : 'text-green-600'
                    }`}>
                      Força: {strengthLabel}
                    </p>
                  </div>
                )}

                {/* Requirements */}
                <ul className="space-y-1 mt-1">
                  {[
                    { ok: strength[0], label: 'Pelo menos 8 caracteres' },
                    { ok: strength[1], label: 'Uma letra maiúscula' },
                    { ok: strength[2], label: 'Um número' },
                  ].map(({ ok, label }) => (
                    <li key={label} className={`flex items-center gap-1.5 text-xs ${ok ? 'text-green-600' : 'text-muted-foreground'}`}>
                      <CheckCircle className={`h-3.5 w-3.5 ${ok ? 'text-green-500' : 'text-muted-foreground/40'}`} />
                      {label}
                    </li>
                  ))}
                </ul>

                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password.message}</p>
                )}
              </div>

              {/* Confirm password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar nova senha</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Repita a senha"
                    className="pr-10"
                    {...register('confirmPassword')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
                )}
              </div>

              <Button type="submit" className="w-full h-11" disabled={isSubmitting}>
                {isSubmitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redefinindo...</>
                ) : (
                  'Redefinir senha'
                )}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Lembrou a senha?{' '}
                <Link to="/login" className="text-primary hover:underline font-medium">
                  Fazer login
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
